import { createContext, memo, useCallback, useContext, useMemo, useState } from 'react';
import { omit } from 'remeda';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useResourceContext } from 'react-admin';
import { hasLength } from 'utility/functions';
import {
  QPArrayFormat,
  qpUpdateHelper,
} from 'utility/hooks/useStateWithDebouncedCallback';
import { useSearch } from '../../SearchContextProvider';
import { SearchAdvancedContextType, ValidListFilterType, PartialVLF } from '../../types';
import { hasPrintPrivilege } from 'utility/privileges';
import { getRepeatQpConfig } from 'components/Search/functions';

/**
 * --------------------------------------
 * ADVANCED SEARCH CONTEXT PROVIDER
 * --------------------------------------
 */
export const SearchAdvancedContext = createContext<SearchAdvancedContextType | undefined>(
  undefined,
);

export const useSearchAdvanced = () => {
  const context = useContext(SearchAdvancedContext);
  if (!context) {
    throw new Error('useSearchAdvanced must be used within an SearchAdvancedProvider');
  }
  return context;
};

type SearchAdvancedContextProviderProps = {
  children: React.ReactNode;
};

export const SearchAdvancedContextProvider = memo(
  ({ children }: SearchAdvancedContextProviderProps) => {
    const {
      updateQueryParams,
      validListFilter,
      subEnabled,
      accountType,
      preservedKeysArray,
    } = useSearch();
    const resource = useResourceContext();
    const { sessionConfig } = useCosmos();

    const [stagedFilters, setStagedFilters] = useState<PartialVLF>(() =>
      omit(validListFilter, preservedKeysArray),
    );

    // Multi site search specific
    const [allDomainsCache, setAllDomainsCache] = useState<string[] | null>(null);
    const [searchDomains, setSearchDomains] = useState<string[]>([]);

    // User Search Specific
    const isNormal = accountType === 'normal';
    const hasSubExpireStartOrEndTime =
      hasLength(validListFilter?.['subscription_expire_time[max]']) ||
      hasLength(validListFilter?.['subscription_expire_time[min]']);
    const hasSubRateRangeMinMax =
      hasLength(validListFilter?.['subscription_rate_price_range[min]']) ||
      hasLength(validListFilter?.['subscription_rate_price_range[max]']);
    const hasSubStartRangeMinMax =
      hasLength(validListFilter?.['subscription_start_time[min]']) ||
      hasLength(validListFilter?.['subscription_start_time[max]']);
    const hasPrintDepthRangeMinMax =
      hasLength(validListFilter?.['print_depth[min]']) ||
      hasLength(validListFilter?.['print_depth[max]']);
    const hasPrintPublicationDateMinMax =
      hasLength(validListFilter?.['print_publication_date[min]']) ||
      hasLength(validListFilter?.['print_publication_date[max]']);

    // check for showing print options
    const totalCMS = hasPrintPrivilege(sessionConfig);

    const isTransactionsResource = resource === 'pay/transactions';
    const isDashboardResource = resource === 'insights/dashboard';

    const showFilters = useMemo(() => {
      return {
        //Universal Search Specific
        showFlag: hasLength(validListFilter?.flag),
        // For dashboards, type is part of the main dropdown UI, not an advanced filter
        showType: !isDashboardResource && hasLength(validListFilter?.type),

        //Editorial Search Specific
        showAuthor: hasLength(validListFilter?.author),
        showSection: hasLength(validListFilter?.section),
        showPublishStatus: hasLength(validListFilter?.publish_status),
        showDraft: hasLength(validListFilter?.draft),
        showAdvertorial: hasLength(validListFilter?.advertorial),
        showApp: hasLength(validListFilter?.app),
        showContributed: hasLength(validListFilter?.contributed),
        showDeleted: hasLength(validListFilter?.deleted),
        showTemplate: hasLength(validListFilter?.template),
        showPhotoSales: hasLength(validListFilter?.photo_sales),
        showPrintSlug: hasLength(validListFilter?.print_slug),
        showPrintReady: Boolean(totalCMS && hasLength(validListFilter?.print_ready)),
        showPrintPublication: Boolean(
          totalCMS && hasLength(validListFilter?.print_publication),
        ),
        showPrintPublicationDateRange: Boolean(totalCMS && hasPrintPublicationDateMinMax),
        showPrintPublicationStatus: Boolean(
          totalCMS && hasLength(validListFilter?.print_publication_status),
        ),
        showPrintDepthRange: Boolean(totalCMS && hasPrintDepthRangeMinMax),
        showStartTime:
          hasLength(validListFilter?.['start_time[max]']) ||
          hasLength(validListFilter?.['start_time[min]']),
        showWorkflow: hasLength(validListFilter?.workflow),

        // User Search Specific
        showBanned: hasLength(validListFilter?.banned),
        showAccStatus: hasLength(validListFilter?.account_status),
        showAuthType: hasLength(validListFilter?.auth_type),

        // Subscription inputs
        showViewBy: subEnabled && isNormal && hasLength(validListFilter?.view_by),
        showSubState:
          subEnabled && isNormal && hasLength(validListFilter?.subscription_state),
        showSubService:
          subEnabled && isNormal && hasLength(validListFilter?.subscription_service),
        showSubServiceType:
          subEnabled && isNormal && hasLength(validListFilter?.subscription_service_type),

        // Not an actual API param, see VLF type comments.
        subRateNames: subEnabled && isNormal && hasLength(validListFilter?.subRateNames),

        showSubStartRange: subEnabled && isNormal && hasSubStartRangeMinMax,
        showSubExpireDateRange: subEnabled && isNormal && hasSubExpireStartOrEndTime,
        showSubRatePriceRange: subEnabled && isNormal && hasSubRateRangeMinMax,

        /**
         * Syndication Search Specific
         *
         * Due to the nature of Syndication, these
         * checks also occur in the syndication component for Advanced Search, but
         * these are still needed for the reset filters button.
         */
        showDriver: hasLength(validListFilter?.driver),
        showSlug: hasLength(validListFilter?.slug),
        showCategory: hasLength(validListFilter?.category),
        showSubject: hasLength(validListFilter?.subject),
        showIndustry: hasLength(validListFilter?.industry),
        showLanguage: hasLength(validListFilter?.language),
        showGeography: hasLength(validListFilter?.geography),
        showPlatform: hasLength(validListFilter?.platform),
        showIncludeNonPublishable: hasLength(validListFilter?.include_nonpublishable),
        showFeed: hasLength(validListFilter?.feed),
        showPostType: hasLength(validListFilter?.post_type),
        showMediaType: hasLength(validListFilter?.media_type),
        showEventType: hasLength(validListFilter?.eventType),
        showSafeSearch: hasLength(validListFilter?.safeSearch),
        showVmsMediaType: hasLength(validListFilter?.vms_media_type),
        showReutersTypes: hasLength(validListFilter?.reuters_types),
        showDateRange:
          hasLength(validListFilter?.['date_range[min]']) ||
          hasLength(validListFilter?.['date_range[max]']),
        showModDateRange:
          hasLength(validListFilter?.['mod_date_range[min]']) ||
          hasLength(validListFilter?.['mod_date_range[max]']),
        showOrderRange:
          hasLength(validListFilter?.['order_range[min]']) ||
          hasLength(validListFilter?.['order_range[max]']),
        showStartRange:
          hasLength(validListFilter?.['start_range[min]']) ||
          hasLength(validListFilter?.['start_range[max]']),
        showEndRange:
          hasLength(validListFilter?.['end_range[min]']) ||
          hasLength(validListFilter?.['end_range[max]']),
        showRunDate: hasLength(validListFilter?.['date']),
        // Services Search Specific
        showServicesHasRates: hasLength(validListFilter?.has_rates),
        showServicesIsActive: hasLength(validListFilter?.is_active),
        showServicesIsTestmode: hasLength(validListFilter?.is_testmode),
        showServicesIsHidden: hasLength(validListFilter?.is_hidden),
        showServicesIsDeleted: hasLength(validListFilter?.is_deleted),

        // Email Reach Search Specific
        lists: hasLength(validListFilter?.lists),
        filter: hasLength(validListFilter?.filter),
        list_type: hasLength(validListFilter?.list_type),
        campaign_type: hasLength(validListFilter?.campaign_type),
        ids: hasLength(validListFilter?.ids),
        status: hasLength(validListFilter?.status),

        // Transactions Search Specific
        showTransactionId: isTransactionsResource && hasLength(validListFilter?.id),
        showTransactionState: isTransactionsResource && hasLength(validListFilter?.state),
        showTransactionTestStatus:
          isTransactionsResource && hasLength(validListFilter?.test_status),
        showTransactionDateRange:
          isTransactionsResource &&
          (hasLength(validListFilter?.['transaction_date_range[min]']) ||
            hasLength(validListFilter?.['transaction_date_range[max]'])),

        // Notifier Search Specific
        showSendStatus: hasLength(validListFilter?.send_status),
        showChannels: hasLength(validListFilter?.channels),
        showSendDate:
          hasLength(validListFilter?.['send_date[min]']) ||
          hasLength(validListFilter?.['send_date[max]']),
      };
    }, [
      isDashboardResource,
      isTransactionsResource,
      validListFilter,
      subEnabled,
      isNormal,
      totalCMS,
      hasSubRateRangeMinMax,
      hasSubExpireStartOrEndTime,
      hasSubStartRangeMinMax,
      hasPrintDepthRangeMinMax,
      hasPrintPublicationDateMinMax,
    ]);

    // calculate the count of advanced filters
    const advancedFiltersCount = Object.values(showFilters).reduce(
      (count, show) => count + (show ? 1 : 0),
      0,
    );

    const updateStagedFilterValue = useCallback(
      (key: keyof ValidListFilterType, value: string | string[]) => {
        setStagedFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
      },
      [],
    );

    /**
     * Handle qp updates for string / array values. (not staged)
     */
    const handleQpValueUpdate = useCallback(
      (qpKey: string, value: string | string[], arrayFormat?: QPArrayFormat) => {
        qpUpdateHelper(qpKey, value, updateQueryParams, arrayFormat);
      },
      [updateQueryParams],
    );

    /**
     * Handle repeat qp keys for edge cases where
     * we use `getAll()` to parse / set repeat params.
     */
    const repeatQpKeys: string[] = useMemo(() => {
      const { keys } = getRepeatQpConfig({ resource: resource });
      return keys;
    }, [resource]);

    /**
     * Apply staged filters value to query params
     * format with similar logic to qpUpdateHelper
     */
    const applyFilters = useCallback(() => {
      updateQueryParams((prevQueryParams) => {
        // Create new object from previous query params
        const newQueryParams = new URLSearchParams(prevQueryParams.toString());

        // Update QP from staged filters, string, array, or delete key.
        Object.entries(stagedFilters).forEach(([key, value]) => {
          // Clear existing values for this key
          newQueryParams.delete(key);

          // No value - key already deleted
          if (!hasLength(value)) {
            return;
          }

          // Handle single / string values directly
          if (typeof value === 'string') {
            newQueryParams.set(key, value);
            return;
          }

          // Handle array values - repeat vs comma format
          if (Array.isArray(value)) {
            if (repeatQpKeys.includes(key)) {
              // Repeat query param format (edge cases)
              // eg: /?key=value1&key=value2 - (values may contain commas, etc.)
              // parse: qp.getAll('key')
              value.forEach((item) => {
                if (item) {
                  newQueryParams.append(key, item);
                }
              });
            } else {
              // Default (common) - comma joined under one qp key
              // eg: /?key=value1,value2 - (values may not contain delimiter)
              // parse: qp.get('key').split(',')
              newQueryParams.set(key, value.join(','));
            }
            return;
          }

          // No case matched, log warning.
          console.warn(
            '[SearchAdvancedContextProvider/applyFilters] unhandled kv:',
            key,
            value,
          );
        });

        return newQueryParams;
      });
    }, [repeatQpKeys, stagedFilters, updateQueryParams]);

    const contextValue = useMemo(() => {
      return {
        isNormal,
        stagedFilters,
        showFilters,
        advancedFiltersCount,
        setStagedFilters,
        updateStagedFilterValue,
        handleQpValueUpdate,
        applyFilters,
        hasSubRateRangeMinMax,
        hasSubExpireStartOrEndTime,
        totalCMS,
        allDomainsCache,
        setAllDomainsCache,
        searchDomains,
        setSearchDomains,
      };
    }, [
      isNormal,
      stagedFilters,
      showFilters,
      advancedFiltersCount,
      setStagedFilters,
      updateStagedFilterValue,
      handleQpValueUpdate,
      applyFilters,
      hasSubRateRangeMinMax,
      hasSubExpireStartOrEndTime,
      totalCMS,
      allDomainsCache,
      setAllDomainsCache,
      searchDomains,
      setSearchDomains,
    ]);

    return (
      <SearchAdvancedContext.Provider value={contextValue}>
        {children}
      </SearchAdvancedContext.Provider>
    );
  },
);
SearchAdvancedContextProvider.displayName = 'SearchAdvancedContextProvider';
